import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

class DigitalGiftCard extends HTMLElement {
  connectedCallback() {
    this.amountButtons = Array.from(this.querySelectorAll('[data-gift-card-amount]'));
    this.titleElement = this.querySelector('[data-gift-card-title]');
    this.proceedButton = this.querySelector('[data-gift-card-proceed]');
    this.submitContainer = this.querySelector('[data-gift-card-submit-container]');
    this.submitButton = this.querySelector('[data-gift-card-submit]');
    this.messageInput = this.querySelector('[data-gift-card-message]');
    this.characterCount = this.querySelector('[data-gift-card-count]');
    this.dateSelect = this.querySelector('[data-gift-card-date-display]');
    this.sendOnInput = this.querySelector('[data-gift-card-send-on]');
    this.timezoneOffsetInput = this.querySelector('[data-gift-card-timezone-offset]');
    this.errorMessage = this.querySelector('[data-gift-card-error]');
    this.dialogComponent = this.querySelector('dialog-component');
    this.productFormComponent = this.closest('product-form-component');
    this.form = this.closest('form');
    this.variantInput = this.productFormComponent?.querySelector('[ref="variantId"]');
    this.variantAvailability = this.querySelector('variant-availability');
    this.wishlistSlot = this.querySelector('.klaviyo-favorites-plp-slot');

    this.onAmountClick = (event) => this.selectAmount(event.currentTarget);
    this.onProceedClick = () => this.showScreen('details');
    this.onMessageInput = () => this.updateCharacterCount();
    this.onDateChange = () => this.syncDate();
    this.onMirrorInput = () => this.syncAdminMirrors();
    this.onRequiredFieldInput = (event) => this.clearFieldValidation(event.currentTarget);
    this.onFormSubmit = (event) => this.validateSubmit(event);
    this.onSubmitClick = (event) => this.validateSubmitClick(event);
    this.onCartAdd = (event) => this.handleCartAdd(event);
    this.onOpenClick = () => this.showScreen('amount');

    this.amountButtons.forEach((button) => button.addEventListener('click', this.onAmountClick));
    this.proceedButton?.addEventListener('click', this.onProceedClick);
    this.messageInput?.addEventListener('input', this.onMessageInput);
    this.dateSelect?.addEventListener('change', this.onDateChange);
    this.querySelectorAll('[data-gift-card-mirror-source]').forEach((field) => {
      field.addEventListener('input', this.onMirrorInput);
    });
    this.querySelectorAll('[data-gift-card-required]').forEach((field) => {
      field.addEventListener('input', this.onRequiredFieldInput);
      field.addEventListener('change', this.onRequiredFieldInput);
    });
    this.submitButton?.addEventListener('click', this.onSubmitClick, true);
    this.form?.addEventListener('submit', this.onFormSubmit, true);
    document.addEventListener('cart:add', this.onCartAdd);
    this.querySelector('[data-gift-card-open]')?.addEventListener('click', this.onOpenClick);

    this.populateDateOptions();
    this.setDefaultDate();
    this.updateCharacterCount();
    this.syncAdminMirrors();

    const selectedButton =
      this.amountButtons.find((button) => button.getAttribute('aria-pressed') === 'true' && !button.disabled) ||
      this.amountButtons.find((button) => !button.disabled);

    if (selectedButton) {
      this.selectAmount(selectedButton);
    } else {
      this.proceedButton?.setAttribute('disabled', 'disabled');
      this.submitButton?.setAttribute('disabled', 'disabled');
    }

    this.showScreen('amount');
  }

  disconnectedCallback() {
    this.amountButtons?.forEach((button) => button.removeEventListener('click', this.onAmountClick));
    this.proceedButton?.removeEventListener('click', this.onProceedClick);
    this.messageInput?.removeEventListener('input', this.onMessageInput);
    this.dateSelect?.removeEventListener('change', this.onDateChange);
    this.querySelectorAll('[data-gift-card-mirror-source]').forEach((field) => {
      field.removeEventListener('input', this.onMirrorInput);
    });
    this.querySelectorAll('[data-gift-card-required]').forEach((field) => {
      field.removeEventListener('input', this.onRequiredFieldInput);
      field.removeEventListener('change', this.onRequiredFieldInput);
    });
    this.submitButton?.removeEventListener('click', this.onSubmitClick, true);
    this.form?.removeEventListener('submit', this.onFormSubmit, true);
    document.removeEventListener('cart:add', this.onCartAdd);
    this.querySelector('[data-gift-card-open]')?.removeEventListener('click', this.onOpenClick);
  }

  selectAmount(button) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    this.amountButtons.forEach((amountButton) => {
      amountButton.setAttribute('aria-pressed', amountButton === button ? 'true' : 'false');
    });

    if (this.variantInput instanceof HTMLInputElement && button.dataset.variantId) {
      this.variantInput.value = button.dataset.variantId;
    }

    this.syncVariantDependentUi(button);

    this.proceedButton?.removeAttribute('disabled');
    this.submitButton?.removeAttribute('disabled');
  }

  syncVariantDependentUi(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) return;

    if (this.variantAvailability instanceof HTMLElement) {
      this.variantAvailability.dataset.variantId = variantId;
    }

    if (this.wishlistSlot instanceof HTMLElement) {
      this.wishlistSlot.dataset.variantId = variantId;
    }

    const variant = {
      id: variantId,
      available: !button.disabled,
    };
    const productId = this.productFormComponent?.dataset.productId || '';

    this.dispatchEvent(new VariantSelectedEvent(variant));
    this.dispatchEvent(
      new VariantUpdateEvent(variant, this.dataset.defaultVariantId || variantId, {
        productId,
      })
    );
  }

  showScreen(screenName) {
    this.querySelectorAll('[data-gift-card-screen]').forEach((screen) => {
      screen.hidden = screen.getAttribute('data-gift-card-screen') !== screenName;
    });

    if (this.titleElement) {
      this.titleElement.textContent =
        screenName === 'details'
          ? this.dataset.detailsTitle || ''
          : this.dataset.amountTitle || '';
    }

    if (this.proceedButton) {
      this.proceedButton.hidden = screenName === 'details';
    }

    if (this.submitContainer) {
      this.submitContainer.hidden = screenName !== 'details';
    }

    if (screenName === 'details') {
      this.setDefaultDate();
      requestAnimationFrame(() => {
        this.querySelector('[data-gift-card-required]')?.focus();
      });
    } else {
      this.clearValidation();
    }
  }

  setDefaultDate() {
    if (this.timezoneOffsetInput instanceof HTMLInputElement) {
      this.timezoneOffsetInput.value = new Date().getTimezoneOffset().toString();
    }

    if (!(this.sendOnInput instanceof HTMLInputElement)) return;

    if (this.dateSelect instanceof HTMLSelectElement) {
      if (!this.dateSelect.value) {
        this.dateSelect.selectedIndex = 0;
      }

      this.syncDate();
      return;
    }

    if (!this.sendOnInput.value) {
      this.sendOnInput.value = this.formatDate(new Date());
    }
  }

  formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  populateDateOptions() {
    if (!(this.dateSelect instanceof HTMLSelectElement) || this.dateSelect.options.length) return;

    for (let dayOffset = 0; dayOffset < 31; dayOffset += 1) {
      const date = new Date();
      date.setDate(date.getDate() + dayOffset);

      const option = document.createElement('option');
      option.value = this.formatDate(date);
      option.textContent = this.formatDateLabel(date, dayOffset);
      this.dateSelect.appendChild(option);
    }
  }

  formatDateLabel(date, dayOffset) {
    if (dayOffset === 0) return this.dataset.todayLabel || this.formatDate(date);
    if (dayOffset === 1) return this.dataset.tomorrowLabel || this.formatDate(date);

    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  syncDate() {
    if (!(this.dateSelect instanceof HTMLSelectElement) || !(this.sendOnInput instanceof HTMLInputElement)) return;

    this.sendOnInput.value = this.dateSelect.value;
    this.syncAdminMirrors();
  }

  updateCharacterCount() {
    if (!(this.messageInput instanceof HTMLTextAreaElement) || !this.characterCount) return;

    this.characterCount.textContent = `${this.messageInput.value.length}/${this.messageInput.maxLength}`;
    this.syncAdminMirrors();
  }

  syncAdminMirrors() {
    this.querySelectorAll('[data-gift-card-mirror-source]').forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

      const mirrorKey = field.dataset.giftCardMirrorSource;
      const mirror = this.querySelector(`[data-gift-card-mirror="${mirrorKey}"]`);

      if (mirror instanceof HTMLInputElement) {
        mirror.value = field.value;
      }
    });

    const deliveryDateMirror = this.querySelector('[data-gift-card-mirror="delivery-date"]');

    if (deliveryDateMirror instanceof HTMLInputElement && this.dateSelect instanceof HTMLSelectElement) {
      const selectedOption = this.dateSelect.selectedOptions[0];
      deliveryDateMirror.value = selectedOption?.textContent || this.dateSelect.value;
    }
  }

  validateSubmit(event) {
    if (event.submitter && event.submitter !== this.submitButton) return;

    if (this.validateRequiredDetails()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  validateSubmitClick(event) {
    if (this.validateRequiredDetails()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
  }

  validateRequiredDetails() {
    const invalidField = this.getInvalidField();

    if (!invalidField) {
      this.syncAdminMirrors();
      this.clearValidation();
      return true;
    }

    this.showScreen('details');
    this.errorMessage?.classList.add('hidden');
    invalidField.focus();
    return false;
  }

  getInvalidField() {
    const requiredFields = Array.from(this.querySelectorAll('[data-gift-card-required]'));
    let firstInvalidField = null;

    requiredFields.forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

      const isMissing = !field.value.trim();
      const isInvalid = isMissing || !field.checkValidity();
      const fieldError = this.getFieldError(field);

      if (isInvalid) {
        field.setAttribute('aria-invalid', 'true');
        this.updateFieldErrorMessage(field, fieldError, isMissing);
        fieldError?.classList.remove('hidden');
        firstInvalidField ||= field;
      } else {
        field.removeAttribute('aria-invalid');
        fieldError?.classList.add('hidden');
      }
    });

    return firstInvalidField;
  }

  getFieldError(field) {
    const describedBy = field.getAttribute('aria-describedby');

    if (!describedBy) return null;

    const errorId = describedBy.split(/\s+/).find(Boolean);
    const errorElement = errorId ? this.querySelector(`#${CSS.escape(errorId)}`) : null;

    return errorElement?.matches('[data-gift-card-field-error]') ? errorElement : null;
  }

  updateFieldErrorMessage(field, fieldError, isMissing) {
    if (!fieldError) return;

    const emailError = fieldError.dataset.giftCardEmailError;
    const requiredError = fieldError.dataset.giftCardRequiredError;

    if (!isMissing && field instanceof HTMLInputElement && field.type === 'email' && emailError) {
      fieldError.textContent = emailError;
      return;
    }

    if (requiredError) {
      fieldError.textContent = requiredError;
    }
  }

  clearFieldValidation(field) {
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;
    if (!field.value.trim()) return;

    field.removeAttribute('aria-invalid');
    this.getFieldError(field)?.classList.add('hidden');
  }

  showError() {
    this.errorMessage?.classList.remove('hidden');
  }

  clearValidation() {
    this.errorMessage?.classList.add('hidden');
    this.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
    this.querySelectorAll('[data-gift-card-field-error]').forEach((error) => error.classList.add('hidden'));
  }

  handleCartAdd(event) {
    if (event.detail?.data?.didError) return;
    if (event.detail?.data?.productId !== this.productFormComponent?.dataset.productId) return;

    this.dialogComponent?.closeDialog?.();
  }
}

if (!customElements.get('digital-gift-card')) {
  customElements.define('digital-gift-card', DigitalGiftCard);
}
